import { Component, OnDestroy, OnInit } from '@angular/core';
import { ProgramService } from '../../shared/program/service/program.service';
import { Program } from '../../shared/program/model/program';
import { debounceTime, distinctUntilChanged, finalize, map } from 'rxjs/operators';
import { Principal } from '../../shared';
import { HelpService } from '../../shared/service/help.service';
import { JhiLanguageService } from '../../shared/core-utils';
import { CropService } from '../../shared/crop/service/crop.service';
import { Subject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { ProgramContext } from './program.context';
import { NavbarMessageEvent } from '../../shared/model/navbar-message.event';
import { SEARCH_GERMPLASM_LISTS_PERMISSION } from '../../shared/auth/permissions';
import { ProgramUsageService } from '../../shared/service/program-usage.service';
import { ParamContext } from '../../shared/service/param.context';
import { SubscriptionsManager } from '../../shared/util/subscriptions-manager';
import { HttpContext } from '@angular/common/http';
import { SkipErrorInterceptor } from '../../blocks/interceptor/error.interceptor';

@Component({
    selector: 'jhi-program',
    templateUrl: './program.component.html',
    styleUrls: [
        './program.scss'
    ]
})
export class ProgramComponent implements OnInit, OnDestroy {

    MANAGE_STUDIES_VIEW_PERMISSIONS = ['ADMIN', 'STUDIES', 'MANAGE_STUDIES', 'VIEW_STUDIES'];
    SEARCH_GERMPLASM_LISTS_PERMISSION = SEARCH_GERMPLASM_LISTS_PERMISSION;
    PERMISSIONS = [
        ...this.MANAGE_STUDIES_VIEW_PERMISSIONS,
        ...SEARCH_GERMPLASM_LISTS_PERMISSION
    ];

    private readonly SESSION_STORAGE_QUERY_PARAM_KEY = 'bms.queryParams';

    user?: any;

    crops: string[];
    cropName: string;
    cropChanged = new Subject<string>();

    programsById: { [key: string]: Program } = {};
    programModel: string;
    programChanged = new Subject<string>();
    programDropdownOptions: any;

    totalItemCount = 0;
    pageSize = 20;
    pageIterator = 1;

    isLoading = false;
    helpLink: string;

    programItems: any[];

    searchSubject = new Subject<string>();

    subscriptionManager = new SubscriptionsManager();

    constructor(
        private programService: ProgramService,
        private cropService: CropService,
        private principal: Principal,
        private helpService: HelpService,
        private languageService: JhiLanguageService,
        private router: Router,
        private route: ActivatedRoute,
        private paramContext: ParamContext,
        public context: ProgramContext,
        public programUsageService: ProgramUsageService,
    ) {
    }

    ngOnDestroy(): void {
        this.subscriptionManager.unsubscribeAll();
    }

    async ngOnInit() {
        /*
         * specially needed for history.back that comes with programUUID params and no crop,
         * thus causing an error in identity()
         */
        this.paramContext.clear();
        this.user = await this.principal.identity();
        this.crops = await this.cropService.getCrops().toPromise();

        this.programUsageService.getLastestSelectedProgram(this.user.id).toPromise().then((response) => {
            const userProgramSelected = response.body;
            if (userProgramSelected) {
                this.subscriptionManager.add = this.getPrograms(1, undefined).subscribe((res) => {
                    if (this.programsById[userProgramSelected.uniqueID]) {
                        this.programModel = userProgramSelected.uniqueID;
                        this.context.program = userProgramSelected;
                        this.programItems = [{ id: userProgramSelected.uniqueID, label: userProgramSelected.name }];

                        // Load permissions for specific program and crop (to display My Lists/My Studies for users with crop/program role types)
                        if (!this.principal.hasAnyAuthorityDirect(this.MANAGE_STUDIES_VIEW_PERMISSIONS)
                            || !this.principal.hasAnyAuthorityDirect(SEARCH_GERMPLASM_LISTS_PERMISSION)) {
                            this.displayProgramInfo();
                        }
                    }
                }, (err) => {
                });
            }
        });

        this.subscriptionManager.add = this.searchSubject.pipe(
            debounceTime(500),
            distinctUntilChanged()
        ).subscribe((searchTerm) => {
            this.search(searchTerm);
        }
        );
    }

    onSearch(searchTerm: string) {
        this.searchSubject.next(searchTerm);
    }

    search(searchTerm: string): void {
        this.pageIterator = 1;
        this.programItems = [];
        this.subscriptionManager.add = this.getPrograms(this.pageIterator, searchTerm).subscribe((res) => {
            const programs = res.body;
            this.programItems = this.programItems.concat(programs.map(this.mapProgram));
        }, (err) => {
        });
    }

    fetchMore(searchTerm: string): void {
        if (this.canLoadMore()) {
            this.pageIterator = this.pageIterator + 1;
            this.subscriptionManager.add = this.getPrograms(this.pageIterator, searchTerm).subscribe((res) => {
                const programs = res.body;
                this.programItems = this.programItems.concat(programs.map(this.mapProgram));
            }, (err) => {
            });
        }
    }

    private canLoadMore(): boolean {
        return (this.pageIterator * this.pageSize) < this.totalItemCount;
    }

    private getPrograms(page: number, query = '') {
        this.isLoading = true;
        return this.programService.getPrograms(this.cropName, query || '', {
            page: page - 1,
            size: this.pageSize
        }, { context: new HttpContext().set(SkipErrorInterceptor, true) }).pipe(map((res) => {
            this.totalItemCount = Number(res.headers.get('X-Total-Count'));
            Object.assign(this.programsById, res.body.reduce((prev, curr) => {
                prev[curr.uniqueID] = curr;
                return prev;
            }, {}));
            return res;
        })).pipe(
            finalize(() => this.isLoading = false)
        );
    }

    private mapProgram(program): any {
        return {
            id: program.uniqueID,
            label: program.name
        };
    }

    onOpenProgram() {
        const message: NavbarMessageEvent = { programSelected: this.context.program };
        window.parent.postMessage(message, '*');
    }

    async displayProgramInfo() {
        const program = this.context.program;
        // force authority retrieval for specific program
        await this.principal.identity(true, program.crop, program.uniqueID);
        if (this.principal.hasAnyAuthorityDirect(this.MANAGE_STUDIES_VIEW_PERMISSIONS)) {
            this.router.navigate(['my-studies'], {
                relativeTo: this.route,
                queryParams: {
                    programUUID: this.programModel
                }
            });
        } else if (this.principal.hasAnyAuthorityDirect(SEARCH_GERMPLASM_LISTS_PERMISSION)) {
            this.router.navigate(['my-lists'], {
                relativeTo: this.route,
                queryParams: {
                    programUUID: this.programModel
                }
            });
        }
    }

    onCropChange() {
        // workaround to trigger select2 ajax reload
        if (this.cropName) {
            this.programModel = null;
        }
    }

    onProgramChange(programSelected: any): void {
        this.context.program = this.programsById[programSelected];
        this.programItems = [];
        this.pageIterator = 1;
        this.displayProgramInfo();
    }
}
